import React, { useState } from "react";
import { Link } from "react-router-dom";
import MasonryGallery from "../components/MasonryGallery";
import { visibleItems, toolsRow1, toolsRow2, processSteps } from "../data";

const homeGalleryItems = visibleItems.filter(
  (item) =>
    item.categories &&
    (item.categories.includes("Data Graphics") ||
      item.categories.includes("Dashboards & Scrollytelling"))
);

function HeroSection() {
  return (
    <section className="hero">
      <div className="hero__inner container">
        <div className="hero__text fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="hero__content-outer">
            <div className="hero__banner-row">
              <div className="hero__banner-img-wrap">
                <img
                  src="https://res.cloudinary.com/dnx3tgmxg/image/upload/iris_2_edited_ecyuug.png"
                  alt="Airi Iris Ryu"
                  className="hero__banner-img"
                />
              </div>
              <Link to="/contact" className="hero__speech-bubble-link">
                <svg viewBox="0 0 160 80" className="hero__speech-bubble-svg" aria-hidden="true" overflow="visible">
                  <path d="M18,6 C28,2 55,1 85,2 C112,3 135,4 146,10 C154,15 157,24 155,35 C153,46 145,54 132,58 C118,62 98,63 78,62 C62,61 46,60 32,61 C24,62 17,60 13,64 C10,67 11,72 9,71 C6,70 5,65 7,58 C9,52 11,47 9,39 C7,30 8,12 18,6 Z" stroke="#3a3a3a" strokeWidth="2.2" fill="white" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20,8 C30,4 56,3 85,4 C112,5 134,6 144,12 C152,17 155,26 153,36 C151,47 144,54 131,58 C117,62 98,63 78,62" stroke="#3a3a3a" strokeWidth="0.9" fill="none" opacity="0.16" strokeLinecap="round" />
                  <text x="82" y="38" textAnchor="middle" fontFamily="'Avenir', 'Avenir Next', 'Nunito Sans', sans-serif" fontWeight="700" fontSize="14" fill="#3a3a3a">Work with me!</text>
                </svg>
              </Link>
            </div>
            <div className="hero__headline-row">
              <div className="hero__kanji-col">
                <span className="hero__kanji">龍</span>
                <span className="hero__kanji"> 愛</span>
                <span className="hero__kanji">莉</span>
              </div>
              <div className="hero__headline-main">
                <h1 className="hero__headline">Airi (Iris) Ryu<span style={{ color: "var(--c-teal)" }}>.</span></h1>
                <p className="hero__body">
                  Hello! I tell stories about life on earth through data. I have over a decade of experience in data analytics and over 8 years focused on sustainability, climate change and justice topics. I specialise in data visualisations and designing interactive storytelling pieces. Reach out if you would like to work with me or pitch an idea!
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="hero__visual-col fade-up" style={{ animationDelay: "0.25s" }}>
          <VennDiagramSVG />
          <div className="hero__actions">
            <Link to="/portfolio" className="btn btn--primary">View my work</Link>
            <Link to="/contact" className="btn btn--ghost">Get in touch</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const TOOLTIPS = {
  research: {
    bold: "Systems thinking",
    dot: "#e1c1c0",
    rest: " Subject matter expertise working on climate, nature and social research projects — translating complex systems into clear narratives.",
  },
  data: {
    bold: "Giving data meaning",
    dot: "#f8d3ad",
    rest: " 10+ years of data analytics: wrangling messy datasets, enriching raw data, building models, and interpreting data via reports, presentations and dashboards.",
  },
  design: {
    bold: "Making knowledge pretty",
    dot: "#f7e4b0",
    rest: " Designing infographics, data visualisations, interactive dashboards, and scrollytelling journalism is my expertise.",
  },
};

const TOOLTIP_TOOLS = {
  research: ["Desk-based", "Interviews", "Survey analysis", "Statistical analysis", "Literature Review", "Scenario Analysis"],
  data: ["Advanced Excel", "Python", "Flourish", "Tableau", "Power BI", "RawGraphs 2.0"],
  design: ["Figma", "Adobe Illustrator", "Procreate", "React.js", "Powerpoint"],
};

function VennDiagramSVG() {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const wrapperRef = React.useRef(null);

  const opacity = (circle) => (!hovered ? 1 : hovered === circle ? 1 : 0.22);
  const handleEnter = (key) => setHovered(key);
  const handleLeave = () => setHovered(null);
  const handleMouseMove = (e) => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const wobbleStyle = (key) => ({
    transition: "opacity 0.3s ease, transform 0.2s ease",
    transform: hovered === key ? "scale(1.05) rotate(-1.5deg)" : "scale(1) rotate(0deg)",
    transformOrigin: "center center",
  });

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }} onMouseMove={handleMouseMove}>
      <svg viewBox="0 0 520 510" fill="none" xmlns="http://www.w3.org/2000/svg" className="hero__svg" aria-label="Venn diagram: Research, Data Analysis, Visual Design">
        <defs>
          <filter id="glow-blush" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur in="SourceGraphic" stdDeviation="11" result="b" /><feFlood floodColor="#e1c1c0" floodOpacity="0.7" result="c" /><feComposite in="c" in2="b" operator="in" result="s" /><feMerge><feMergeNode in="s" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="glow-peach" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur in="SourceGraphic" stdDeviation="11" result="b" /><feFlood floodColor="#f8d3ad" floodOpacity="0.7" result="c" /><feComposite in="c" in2="b" operator="in" result="s" /><feMerge><feMergeNode in="s" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="glow-cream" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur in="SourceGraphic" stdDeviation="11" result="b" /><feFlood floodColor="#f7e4b0" floodOpacity="0.7" result="c" /><feComposite in="c" in2="b" operator="in" result="s" /><feMerge><feMergeNode in="s" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="glow-teal" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur in="SourceGraphic" stdDeviation="8" result="b" /><feFlood floodColor="#2d807c" floodOpacity="0.55" result="c" /><feComposite in="c" in2="b" operator="in" result="s" /><feMerge><feMergeNode in="s" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>

        <g opacity={opacity("research")} style={wobbleStyle("research")} onMouseEnter={() => handleEnter("research")} onMouseLeave={handleLeave}>
          <ellipse cx="260" cy="168" rx="142" ry="142" fill="transparent" />
          <path d="M253,28 C279,23 312,30 338,47 C366,65 387,95 396,128 C405,161 400,200 382,229 C364,258 334,280 300,290 C266,300 227,298 196,281 C165,264 142,234 131,200 C120,166 121,126 136,96 C151,65 179,42 211,33 C230,27 243,30 253,28 Z" stroke="#3a3a3a" strokeWidth="3" fill="none" filter="url(#glow-blush)" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M251,30 C277,25 310,32 336,49 C363,67 385,96 394,129 C403,162 399,200 381,229 C363,258 333,280 299,290 C265,300 227,298 197,282 C166,265 143,235 132,202 C121,168 122,128 137,98 C151,67 179,44 211,35 C230,29 241,32 251,30 Z" stroke="#3a3a3a" strokeWidth="0.9" fill="none" opacity="0.2" strokeLinecap="round" />
          <rect x="203" y="89" width="115" height="28" rx="0" fill="#e1c1c0" opacity="0.9" />
          <text x="260" y="113" textAnchor="middle" fontFamily="'Fraunces', Georgia, serif" fontWeight="400" fontSize="24" fill="#3a3a3a">Research</text>
        </g>

        <g opacity={opacity("data")} style={wobbleStyle("data")} onMouseEnter={() => handleEnter("data")} onMouseLeave={handleLeave}>
          <ellipse cx="162" cy="328" rx="140" ry="140" fill="transparent" />
          <path d="M155,188 C181,183 214,190 240,207 C268,225 289,255 298,288 C307,321 302,360 284,389 C266,418 236,440 202,450 C168,460 129,458 98,441 C67,424 44,394 33,360 C22,326 23,286 38,256 C53,225 81,202 113,193 C132,187 145,190 155,188 Z" stroke="#3a3a3a" strokeWidth="3" fill="none" filter="url(#glow-peach)" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M153,190 C179,185 212,192 238,209 C265,227 287,256 296,289 C305,322 301,360 283,389 C265,418 236,441 203,451 C169,461 130,459 99,443 C68,426 45,396 34,362 C23,328 24,288 39,258 C54,228 82,205 113,195 C132,189 143,192 153,190 Z" stroke="#3a3a3a" strokeWidth="0.9" fill="none" opacity="0.2" strokeLinecap="round" />
          <rect x="74" y="318" width="74" height="25" rx="0" fill="#f8d3ad" opacity="0.9" />
          <text x="111" y="338" textAnchor="middle" fontFamily="'Fraunces', Georgia, serif" fontWeight="400" fontSize="24" fill="#3a3a3a">Data</text>
        </g>

        <g opacity={opacity("design")} style={wobbleStyle("design")} onMouseEnter={() => handleEnter("design")} onMouseLeave={handleLeave}>
          <ellipse cx="358" cy="328" rx="140" ry="140" fill="transparent" />
          <path d="M365,188 C339,183 306,190 280,207 C252,225 231,255 222,288 C213,321 218,360 236,389 C254,418 284,440 318,450 C352,460 391,458 422,441 C453,424 476,394 487,360 C498,326 497,286 482,256 C467,225 439,202 407,193 C388,187 375,190 365,188 Z" stroke="#3a3a3a" strokeWidth="3" fill="none" filter="url(#glow-cream)" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M367,190 C341,185 308,192 282,209 C255,227 233,256 224,289 C215,322 219,360 237,389 C255,418 284,441 317,451 C351,461 390,459 421,443 C452,426 475,396 486,362 C497,328 496,288 481,258 C466,228 438,205 407,195 C388,189 377,192 367,190 Z" stroke="#3a3a3a" strokeWidth="0.9" fill="none" opacity="0.2" strokeLinecap="round" />
          <rect x="359" y="340" width="94" height="25" rx="0" fill="#f7e4b0" opacity="0.9" />
          <text x="406" y="360" textAnchor="middle" fontFamily="'Fraunces', Georgia, serif" fontWeight="400" fontSize="24" fill="#3a3a3a">Design</text>
        </g>

        <rect x="238" y="260" width="45" height="25" rx="0" fill="#2d807c" opacity="0.88" filter="url(#glow-teal)" />
        <text x="260" y="279" textAnchor="middle" fontFamily="'Fraunces', Georgia, serif" fontWeight="700" fontSize="24" fill="#ffffff">me</text>
      </svg>

      {/* HTML tooltip — positioned relative to wrapper div */}
      {hovered && (
        <div
          className="venn-tooltip"
          style={{
            left: mousePos.x - 260,
            top: mousePos.y,
          }}
        >
                    <p className="venn-tooltip__desc">
            <strong>{TOOLTIPS[hovered].bold}</strong>
            <span
              className="venn-tooltip__dot" font-weight="700"
              style={{ color: TOOLTIPS[hovered].dot }}
            >.</span>
            </p>
            <p className="venn-tooltip__desc">
            {TOOLTIPS[hovered].rest}
          </p>
          <div className="venn-tooltip__divider" />
          <p className="venn-tooltip__tools-label">Tools</p>
          <div className="venn-tooltip__tools">
            {TOOLTIP_TOOLS[hovered].map((tool, i) => (
              <span key={i} className="venn-tooltip__tool">{tool}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Map each tool name to its hover underline colour class */
const TOOL_COLOUR = {
  "Figma": "yellow",
  "Adobe Illustrator": "yellow",
  "Procreate": "yellow",
  "RawGraphs 2.0": "yellow",
  "Powerpoint": "yellow",
  "Excel": "orange",
  "Python": "orange",
  "Flourish": "pink",
  "Tableau": "teal",
  "Power BI": "teal",
  "React.js": "teal",
};

/* ─── SECTION 2 — Tools (static, hover effect) ─── */
function ToolsSection() {
  return (
    <section className="tools">
      <div className="tools__inner">
        <ToolsStrip items={toolsRow1} label="Design & Creative" labelColour="yellow" />
        <ToolsStrip items={toolsRow2} label="Data & Code" labelColour="cream" />
      </div>
    </section>
  );
}

function ToolsStrip({ items, label, labelColour }) {
  return (
    <div className="tools__strip">
      <div className={`tools__label tools__label--${labelColour}`}>{label}</div>
      <div className="tools__list">
        {items.map((tool, i) => {
          const colour = TOOL_COLOUR[tool.name] || "teal";
          return (
            <div key={i} className={`tools__item tools__item--${colour}`}>
              <span className="tools__name">{tool.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── SECTION 3 — Portfolio CTA ─── */
function PortfolioCTASection() {
  return (
    <section className="portfolio-cta">
      <div className="portfolio-cta__inner container">
        <div className="portfolio-cta__left">
          <h2 className="portfolio-cta__heading">Explore my<br />Portfolio</h2>
        </div>
        <div className="portfolio-cta__right">
          <p className="portfolio-cta__body">
            Although my biggest strength is when I can use all three of my expertise, I also do focused work on research, design, and analytics.
          </p>
          <Link to="/portfolio" className="portfolio-cta__link">
            View my full portfolio<span className="portfolio-cta__arrow" aria-hidden="true"> →</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION 4 — Process ─── */
const STEP_COLOURS = ["var(--c-cream)", "var(--c-peach)", "var(--c-orange)", "var(--c-rose)", "var(--c-teal)"];
const STEP_COLOURS_HEX = ["#f7e4b0", "#f8d3ad", "#eb8015", "#a65e5d", "#2d807c"];

const STAGGER_PX = [0, 120, 0, 120, 0];

function ProcessSection() {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 580;
  return (
    <section className="process">
      <div className="container">
        <h2 className="process__heading">My Flow</h2>
        <div className="process__steps">
          {processSteps.map((step, i) => (
            <div
              className="process__step"
              key={step.number}
              style={{ marginTop: isMobile ? 0 : STAGGER_PX[i] }}
            >
              <div className="process__step-label">
                <span className="process__step-word">Step </span>
                <span className="process__step-num" style={{ color: STEP_COLOURS[i] }}>
                  {i + 1}
                </span>
              </div>
              <div className="process__step-text">
                <h3 className="process__step-title">{step.title}</h3>
                <p className="process__step-body">{step.body}</p>
              </div>
              <div className="process__step-dots">
                {Array.from({ length: i + 1 }).map((_, d) => (
                  <span key={d} className="process__step-dot" style={{ background: STEP_COLOURS[i] }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION 5 — Gallery ─── */
function GallerySection() {
  return (
    <section className="gallery-section">
      <div className="container">
        <div className="gallery-section__header">
          <h2 className="gallery-section__heading"> </h2>
          <Link to="/portfolio" className="gallery-section__all">See all →</Link>
        </div>
        <MasonryGallery allItems={homeGalleryItems} filteredItems={homeGalleryItems} />
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <HeroSection />
      <ToolsSection />
      <PortfolioCTASection />
      <ProcessSection />
      <GallerySection />
    </>
  );
}